import React, { useCallback, useMemo } from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { focus } from '#src/app/shared/utils/dom';

import type { SectionId } from '../../../../hooks/useScrollToSection';
import { useScrollToSection } from '../../../../hooks/useScrollToSection';

function counterCountAsNumber(count: number | string): number | null {
	if (typeof count === 'number') return count;
	const numeric = Number(String(count).replace(/,/g, ''));
	return Number.isFinite(numeric) ? numeric : null;
}

type CounterUpdateBaseProps = {
	/**
	 * The label text to display (e.g., "shares", "comments")
	 */
	label: string;
	/**
	 * The count value to display (number or string)
	 */
	count: number | string;
	/**
	 * Optional test ID for testing purposes
	 */
	testId?: string;
	/**
	 * Optional QA ID for QA automation
	 */
	qaId?: string;
	/**
	 * Optional children to render alongside the counter (e.g., video preview icon)
	 */
	children?: React.ReactNode;
	/**
	 * When true, on mobile the label is stacked under the count/children (column layout).
	 * Use when media mentions are present so each counter shows label below on small screens.
	 */
	stackLabelOnMobile?: boolean;
};

export type CounterUpdateProps =
	| (CounterUpdateBaseProps & {
			sectionId: SectionId;
			onTrackClick: () => void;
			onClick?: undefined;
	  })
	| (CounterUpdateBaseProps & {
			sectionId?: undefined;
			onTrackClick?: undefined;
			onClick?: () => void;
	  });

export const CounterUpdate: React.FC<CounterUpdateProps> = ({
	label,
	count,
	testId,
	qaId,
	sectionId,
	onTrackClick,
	onClick,
	children,
	stackLabelOnMobile = false,
}) => {
	const { localizeNumber } = useI18n();
	const displayCount = useMemo(() => {
		const numeric = counterCountAsNumber(count);
		if (numeric === null) return String(count);
		return localizeNumber(numeric, { maximumFractionDigits: 0 });
	}, [count, localizeNumber]);
	const {
		actions: { scrollToSection },
	} = useScrollToSection();

	const handleClick = useCallback(() => {
		if (sectionId && onTrackClick) {
			onTrackClick();
			scrollToSection(sectionId);
			focus(document.querySelector<HTMLElement>(`#${sectionId}`), { makeFocusable: true, preventScroll: true });
		} else if (onClick) {
			onClick();
		}
	}, [scrollToSection, sectionId, onTrackClick, onClick]);

	const isClickable = !!sectionId || !!onClick;

	const countElement = (
		<Text
			data-testid={testId ? `${testId}-count` : undefined}
			data-qa={qaId ? `${qaId}-count` : undefined}
			variant="body"
			size="large"
			fontWeight="bold"
			className={cn('md:text-size-body-xl-bold! text-[#403A49]', isClickable && 'md:cursor-pointer md:hover:underline')}
		>
			{displayCount}
		</Text>
	);

	const content: React.ReactNode = isClickable ? (
		<Button
			variant="tertiary"
			onClick={handleClick}
			data-testid={testId ? `${testId}-button` : undefined}
			data-qa={qaId ? `${qaId}-button` : undefined}
			aria-label={`${displayCount} ${label}`}
			className="border-0 bg-transparent p-0 no-underline"
		>
			{countElement}
		</Button>
	) : (
		countElement
	);

	return (
		<div
			className={cn(
				'box-border flex w-full items-center gap-[10px] pl-[14px] lg:ml-[44px] lg:h-[48px] lg:h-auto lg:flex-col-reverse lg:items-start lg:gap-4 lg:pt-[29px] lg:pb-[23px] lg:pl-0',
				stackLabelOnMobile ? 'mb-3 flex-col items-start sm:gap-2' : 'flex-row',
			)}
			data-testid={testId}
			data-qa={qaId}
		>
			<div className="flex flex-row gap-2">
				{content}
				{children}
			</div>
			<Text variant="body" size="large" className="md:text-size-body-small! font-light text-[#403A49]">
				{label}
			</Text>
		</div>
	);
};
